
import React, { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { DashboardHeader } from "@/components/admin/DashboardHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Activity,
  RefreshCcw,
  HardDrive,
  Database,
  Settings,
  ShieldCheck,
  Cpu,
  Cloud,
  Clock,
  AlertTriangle,
  FileText,
  Save,
  ServerCrash,
  Power
} from "lucide-react";

const System = () => {
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [isMaintenanceMode, setIsMaintenanceMode] = useState<boolean>(false);
  const [isCacheClearing, setIsCacheClearing] = useState<boolean>(false);
  const [isRestarting, setIsRestarting] = useState<boolean>(false);

  const handleMaintenanceToggle = () => {
    setIsMaintenanceMode(!isMaintenanceMode);
    toast.success(`Maintenance mode ${!isMaintenanceMode ? 'activated' : 'deactivated'}`);
  };

  const handleClearCache = () => {
    setIsCacheClearing(true);
    setTimeout(() => {
      setIsCacheClearing(false);
      toast.success("Cache cleared successfully");
    }, 2000);
  };

  const handleRestartSystem = () => {
    setIsRestarting(true);
    toast.info("System restart initiated...");
    setTimeout(() => {
      setIsRestarting(false);
      toast.success("System restarted successfully");
    }, 3000);
  };

  const handleSaveConfig = () => {
    toast.success("Configuration saved successfully");
  };

  return (
    <AdminLayout>
      <div className="flex flex-col w-full h-full">
        <DashboardHeader 
          userName="Admin" 
          fullName="System Administrator" 
          email="admin@hoss-immo.de" 
        />
        
        <div className="p-6">
          <Tabs 
            value={activeTab} 
            onValueChange={setActiveTab}
            className="w-full"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold">System Administration</h2>
              
              <TabsList>
                <TabsTrigger value="overview">
                  <Activity className="mr-2 h-4 w-4" />
                  Overview
                </TabsTrigger>
                <TabsTrigger value="configuration">
                  <Settings className="mr-2 h-4 w-4" />
                  Configuration
                </TabsTrigger>
                <TabsTrigger value="backup">
                  <HardDrive className="mr-2 h-4 w-4" />
                  Backup & Recovery
                </TabsTrigger>
                <TabsTrigger value="maintenance">
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Maintenance
                </TabsTrigger>
              </TabsList>
            </div>
            
            <TabsContent value="overview" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Cpu className="mr-2 h-5 w-5 text-[#41c7f0]" />
                      System Status
                    </CardTitle>
                    <CardDescription>Current system performance</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">CPU Usage</span>
                        <span className="font-medium">42%</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div className="bg-[#41c7f0] h-2 rounded-full" style={{ width: "42%" }}></div>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Memory Usage</span>
                        <span className="font-medium">2.8 GB / 8 GB</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div className="bg-[#41c7f0] h-2 rounded-full" style={{ width: "35%" }}></div>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Disk Usage</span>
                        <span className="font-medium">138 GB / 500 GB</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div className="bg-[#41c7f0] h-2 rounded-full" style={{ width: "28%" }}></div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <ShieldCheck className="mr-2 h-5 w-5 text-[#41c7f0]" />
                      Security Status
                    </CardTitle>
                    <CardDescription>System security overview</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Firewall</span>
                        <span className="bg-green-500/20 text-green-500 px-2 py-1 rounded text-xs font-medium">Active</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Antivirus</span>
                        <span className="bg-green-500/20 text-green-500 px-2 py-1 rounded text-xs font-medium">Updated</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Last Security Scan</span>
                        <span className="text-gray-300">Today, 08:30</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">SSL Certificate</span>
                        <span className="bg-green-500/20 text-green-500 px-2 py-1 rounded text-xs font-medium">Valid</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Pending Updates</span>
                        <span className="text-yellow-500 font-medium">3</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Cloud className="mr-2 h-5 w-5 text-[#41c7f0]" />
                      Services
                    </CardTitle>
                    <CardDescription>Status of system services</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Web Server</span>
                        <span className="bg-green-500/20 text-green-500 px-2 py-1 rounded text-xs font-medium">Running</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Database</span>
                        <span className="bg-green-500/20 text-green-500 px-2 py-1 rounded text-xs font-medium">Running</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Email Service</span>
                        <span className="bg-green-500/20 text-green-500 px-2 py-1 rounded text-xs font-medium">Running</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Backup Service</span>
                        <span className="bg-green-500/20 text-green-500 px-2 py-1 rounded text-xs font-medium">Running</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Cron Jobs</span>
                        <span className="bg-green-500/20 text-green-500 px-2 py-1 rounded text-xs font-medium">Running</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="col-span-1 md:col-span-2 lg:col-span-3">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Clock className="mr-2 h-5 w-5 text-[#41c7f0]" />
                      System Events
                    </CardTitle>
                    <CardDescription>Recent system activity</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-start gap-4 p-3 rounded-lg border border-gray-800 bg-gray-900/50">
                        <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5" />
                        <div>
                          <p className="font-medium">Security update available</p>
                          <p className="text-sm text-gray-400">System security update v3.2.1 is available for installation</p>
                          <p className="text-xs text-gray-500 mt-1">Today, 14:32</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-4 p-3 rounded-lg border border-gray-800 bg-gray-900/50">
                        <ServerCrash className="h-5 w-5 text-red-500 mt-0.5" />
                        <div>
                          <p className="font-medium">API service restarted</p>
                          <p className="text-sm text-gray-400">API service was automatically restarted after memory issue</p>
                          <p className="text-xs text-gray-500 mt-1">Today, 10:15</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-4 p-3 rounded-lg border border-gray-800 bg-gray-900/50">
                        <FileText className="h-5 w-5 text-blue-500 mt-0.5" />
                        <div>
                          <p className="font-medium">Log rotation completed</p>
                          <p className="text-sm text-gray-400">Weekly log rotation completed. 12 files archived</p>
                          <p className="text-xs text-gray-500 mt-1">Yesterday, 23:00</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="configuration" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Settings className="mr-2 h-5 w-5 text-[#41c7f0]" />
                    System Configuration
                  </CardTitle>
                  <CardDescription>Manage system environment settings</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="app-name">Application Name</Label>
                          <Input id="app-name" placeholder="HOSS Immobilien" defaultValue="HOSS Immobilien" />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="app-url">Application URL</Label>
                          <Input id="app-url" placeholder="https://example.com" defaultValue="https://hoss-immo.de" />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="app-env">Environment</Label>
                          <select
                            id="app-env"
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            defaultValue="production"
                          >
                            <option value="development">Development</option>
                            <option value="staging">Staging</option>
                            <option value="production">Production</option>
                          </select>
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="app-timezone">Timezone</Label>
                          <select
                            id="app-timezone"
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            defaultValue="Europe/Berlin"
                          >
                            <option value="Europe/Berlin">Europe/Berlin</option>
                            <option value="Europe/London">Europe/London</option>
                            <option value="America/New_York">America/New_York</option>
                            <option value="Asia/Tokyo">Asia/Tokyo</option>
                          </select>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="app-locale">Default Locale</Label>
                          <select
                            id="app-locale"
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            defaultValue="de"
                          >
                            <option value="de">German (de)</option>
                            <option value="en">English (en)</option>
                            <option value="fr">French (fr)</option>
                            <option value="es">Spanish (es)</option>
                          </select>
                        </div>
                        
                        <div className="flex items-center justify-between space-y-0 pt-2">
                          <div className="space-y-0.5">
                            <Label htmlFor="debug-mode">Debug Mode</Label>
                            <p className="text-sm text-gray-400">Enable detailed error messages</p>
                          </div>
                          <Switch id="debug-mode" />
                        </div>
                      </div>
                    </div>
                    
                    <div className="pt-4 space-y-2">
                      <Label htmlFor="app-description">Application Description</Label>
                      <Textarea
                        id="app-description"
                        placeholder="Enter description"
                        defaultValue="HOSS Immobilien platform for real estate management and lead processing."
                        className="min-h-[100px]"
                      />
                    </div>
                    
                    <div className="pt-4">
                      <Button onClick={handleSaveConfig} className="bg-[#41c7f0] hover:bg-[#3ab7de]">
                        <Save className="mr-2 h-4 w-4" />
                        Save Configuration
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Database className="mr-2 h-5 w-5 text-[#41c7f0]" />
                    Database Settings
                  </CardTitle>
                  <CardDescription>Manage database connection configuration</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="db-host">Database Host</Label>
                          <Input id="db-host" placeholder="localhost" defaultValue="db.supabase.co" />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="db-name">Database Name</Label>
                          <Input id="db-name" placeholder="database" defaultValue="hoss_production" />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="db-user">Database User</Label>
                          <Input id="db-user" placeholder="username" defaultValue="postgres" />
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="db-password">Database Password</Label>
                          <Input id="db-password" type="password" placeholder="••••••••" defaultValue="••••••••" />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="db-port">Database Port</Label>
                          <Input id="db-port" placeholder="5432" defaultValue="5432" />
                        </div>
                        
                        <div className="flex items-center justify-between space-y-0 pt-2">
                          <div className="space-y-0.5">
                            <Label htmlFor="db-ssl">SSL Connection</Label>
                            <p className="text-sm text-gray-400">Use secure SSL connection</p>
                          </div>
                          <Switch id="db-ssl" defaultChecked />
                        </div>
                      </div>
                    </div>
                    
                    <div className="pt-4">
                      <Button onClick={handleSaveConfig} className="bg-[#41c7f0] hover:bg-[#3ab7de]">
                        <Save className="mr-2 h-4 w-4" />
                        Save Database Settings
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="backup" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <HardDrive className="mr-2 h-5 w-5 text-[#41c7f0]" />
                    Backup Management
                  </CardTitle>
                  <CardDescription>Configure and manage system backups</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-medium">Automatic Backups</h3>
                          <p className="text-sm text-gray-400">Configure scheduled backups</p>
                        </div>
                        <Switch id="auto-backup" defaultChecked />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-2">
                        <div className="space-y-2">
                          <Label htmlFor="backup-frequency">Frequency</Label>
                          <select
                            id="backup-frequency"
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            defaultValue="daily"
                          >
                            <option value="hourly">Hourly</option>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                          </select>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="backup-time">Time</Label>
                          <Input id="backup-time" type="time" defaultValue="03:00" />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="backup-retention">Retention (days)</Label>
                          <Input id="backup-retention" type="number" defaultValue="30" />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="backup-location">Backup Storage Location</Label>
                        <Input id="backup-location" placeholder="/path/to/backups" defaultValue="s3://hoss-backups/production" />
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-800 pt-6">
                      <h3 className="text-lg font-medium mb-4">Recent Backups</h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 rounded-lg border border-gray-800 bg-gray-900/50">
                          <div className="flex items-center gap-3">
                            <HardDrive className="h-5 w-5 text-[#41c7f0]" />
                            <div>
                              <p className="font-medium">Full Backup</p>
                              <p className="text-xs text-gray-400">Database + Files (2.3 GB)</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm">Today, 03:00</p>
                            <div className="flex gap-2 mt-1">
                              <Button variant="outline" size="sm" className="h-7 px-2 text-xs">Restore</Button>
                              <Button variant="outline" size="sm" className="h-7 px-2 text-xs">Download</Button>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between p-3 rounded-lg border border-gray-800 bg-gray-900/50">
                          <div className="flex items-center gap-3">
                            <HardDrive className="h-5 w-5 text-[#41c7f0]" />
                            <div>
                              <p className="font-medium">Full Backup</p>
                              <p className="text-xs text-gray-400">Database + Files (2.2 GB)</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm">Yesterday, 03:00</p>
                            <div className="flex gap-2 mt-1">
                              <Button variant="outline" size="sm" className="h-7 px-2 text-xs">Restore</Button>
                              <Button variant="outline" size="sm" className="h-7 px-2 text-xs">Download</Button>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between p-3 rounded-lg border border-gray-800 bg-gray-900/50">
                          <div className="flex items-center gap-3">
                            <HardDrive className="h-5 w-5 text-[#41c7f0]" />
                            <div>
                              <p className="font-medium">Full Backup</p>
                              <p className="text-xs text-gray-400">Database + Files (2.2 GB)</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm">06.08.2023, 03:00</p>
                            <div className="flex gap-2 mt-1">
                              <Button variant="outline" size="sm" className="h-7 px-2 text-xs">Restore</Button>
                              <Button variant="outline" size="sm" className="h-7 px-2 text-xs">Download</Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="pt-4">
                      <Button className="bg-[#41c7f0] hover:bg-[#3ab7de]">
                        <HardDrive className="mr-2 h-4 w-4" />
                        Create Backup Now
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="maintenance" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <RefreshCcw className="mr-2 h-5 w-5 text-[#41c7f0]" />
                    System Maintenance
                  </CardTitle>
                  <CardDescription>Perform system maintenance tasks</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <Card className="border-gray-800">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg">Maintenance Mode</CardTitle>
                          <CardDescription>Take the application offline for maintenance</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-gray-400">Status: <span className={isMaintenanceMode ? "text-yellow-500" : "text-green-500"}>{isMaintenanceMode ? "Enabled" : "Disabled"}</span></p>
                            </div>
                            <Button 
                              onClick={handleMaintenanceToggle}
                              variant={isMaintenanceMode ? "destructive" : "default"}
                              className={isMaintenanceMode ? "" : "bg-[#41c7f0] hover:bg-[#3ab7de]"}
                            >
                              <Power className="mr-2 h-4 w-4" />
                              {isMaintenanceMode ? "Disable" : "Enable"} Maintenance Mode
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                      
                      <Card className="border-gray-800">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg">Cache Management</CardTitle>
                          <CardDescription>Clear application cache</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-gray-400">Last cleared: 2 hours ago</p>
                            </div>
                            <Button 
                              onClick={handleClearCache} 
                              disabled={isCacheClearing}
                              className="bg-[#41c7f0] hover:bg-[#3ab7de]"
                            >
                              {isCacheClearing ? (
                                <>
                                  <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                                  Clearing...
                                </>
                              ) : (
                                <>
                                  <RefreshCcw className="mr-2 h-4 w-4" />
                                  Clear Cache
                                </>
                              )}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                    
                    <Card className="border-gray-800">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Queue Management</CardTitle>
                        <CardDescription>Manage background job queues</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between p-3 rounded-lg border border-gray-800 bg-gray-900/50">
                            <div>
                              <p className="font-medium">Email Queue</p>
                              <p className="text-sm text-gray-400">12 pending jobs</p>
                            </div>
                            <Button variant="outline" size="sm">Process Now</Button>
                          </div>
                          
                          <div className="flex items-center justify-between p-3 rounded-lg border border-gray-800 bg-gray-900/50">
                            <div>
                              <p className="font-medium">Export Queue</p>
                              <p className="text-sm text-gray-400">0 pending jobs</p>
                            </div>
                            <Button variant="outline" size="sm" disabled>Process Now</Button>
                          </div>
                          
                          <div className="flex items-center justify-between p-3 rounded-lg border border-gray-800 bg-gray-900/50">
                            <div>
                              <p className="font-medium">Import Queue</p>
                              <p className="text-sm text-gray-400">3 pending jobs</p>
                            </div>
                            <Button variant="outline" size="sm">Process Now</Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-gray-800">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">System Restart</CardTitle>
                        <CardDescription>Restart application services</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-400">This will restart all application services</p>
                          </div>
                          <Button 
                            onClick={handleRestartSystem}
                            disabled={isRestarting}
                            variant="destructive"
                          >
                            {isRestarting ? (
                              <>
                                <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                                Restarting...
                              </>
                            ) : (
                              <>
                                <RefreshCcw className="mr-2 h-4 w-4" />
                                Restart System
                              </>
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </AdminLayout>
  );
};

export default System;
